import {Component, Input, OnChanges, OnInit, SimpleChanges} from "@angular/core";
import {UntypedFormBuilder, Validators} from "@angular/forms";
import {Country} from "../../country/country.model";
import {FormComponent} from "../form.component";
import {I18NextService} from "angular-i18next";
import {Profile} from "../../partner/partner.model";
import {Observable, Subscriber} from "rxjs";
import {MessageService} from "primeng/api";

type Form = {
  website: string;
  zip: string;
  city: string;
  country: string;
}

@Component({
    selector: 'app-partner-profile-form',
    templateUrl: './partner-profile-form.component.html',
    styleUrls: [],
    standalone: false
})
export class PartnerProfileFormComponent extends FormComponent<Profile> {

  @Input() public profile: Profile;
  @Input() countries: Country[];
  @Input() submitButtonLabel = 'app_update';

  public fileName: string;
  public newLogo: string; // Data Uri if logo has changed

  constructor(protected fb: UntypedFormBuilder,
              protected i18next: I18NextService,
              private messageService: MessageService) {
    super(fb, i18next);
  }

  public formMapper(form: Form): Profile {
    const profile = new Profile();
    profile.website = form.website;
    profile.zip = form.zip;
    profile.city = form.city;
    profile.country = this.countries.find(country => country.code === form.country);

    if (this.profile?.id) {
      profile.id = this.profile.id;
    }

    if (this.newLogo) {
      profile.logo = this.newLogo;
    }

    return profile;
  }

  getFile(event) {
    this.fileName = event.target.files[0].name;
    if (event.target.files && event.target.files.length > 0) {
      this.resize(event.target.files[0], 720, 720).subscribe({
        next: (r) => {
          this.newLogo = r.replace('data:image/png;base64,', '');
        },
        error: (e) => {
          console.error('Failed to resize image', e);
          this.resetImageChange();
          this.messageService.add({severity: 'error', summary: this.i18next.t('profile_load_image_failed') as string});
        }
      });
    }
  }

  resize(file: File, maxWidth: number, maxHeight: number): Observable<string> {
    return new Observable((observer: Subscriber<string>) => {
      const image = new Image();
      image.onerror = (e) => observer.error(e);
      image.onload = () => {
        const canvas = document.createElement('canvas');

        let width = image.width;
        let height = image.height;
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0, width, height);

        observer.next(canvas.toDataURL('image/png'));
        observer.complete();
      };
      image.src = URL.createObjectURL(file);
    });
  }

  get logo(): string {
    if (this.newLogo) {
      return 'data:image/png;base64,' + this.newLogo;
    } else if (this.profile && this.profile.logo) {
      return 'data:image/png;base64,' + this.profile.logo;
    }
    return '';
  }

  private resetImageChange() {
    this.fileName = '';
  }

  protected createForm() {
    this.form = this.fb.group({
      website: [this.profile?.website ?? '', Validators.required],
      zip: [this.profile?.zip ?? '', Validators.required],
      city: [this.profile?.city ?? '', Validators.required],
      country: [this.profile?.country?.code ?? 'DE', Validators.required],
    })
  }
}
